import React, { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import intervalToDuration from 'date-fns/intervalToDuration';
import { Flex } from 'reflexbox';
import { differenceInDays } from 'date-fns';
import { formatDatetimeWithUtcOffset, isTodayBetween } from '../../libs/dates-lib';
import { fadeIn } from '../../libs/animations-lib';
import RegularText from '../RegularText'
import ShakeContent from '../ShakeContent';
import SandTimeIcon from '../../icons/SandTimeIcon';

/*
* Warning: This FIVE_MINUTES constant is not in seconds.
* The current implementation uses the current 'countdown time' as 'MMDDHHmmss', and converts it to a number.
* where M is months, D is days, H is hours, m is minutes and s is seconds.
* Therefore, 5 minutes remaining would be `Number('000000000500')`
* TODO: Use moment or date-fns library to convert the 'countdown time' to seconds & improve code readability.
* */
const FIVE_MINUTES = 500;
const UTC_ZERO_OFFSET = '+00:00';

const CountDownBox = styled(Flex)`
  height: ${(props) => (props.height ? props.height : '84px')};
  min-width: 210px;
  justify-content: flex-start;
  padding: 8px;
  z-index: 100;
  cursor: default;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.3s;
  border: 1px solid #1d1d1d;
  box-shadow: -3px 3px 3px #00000029;
  background-color: ${(props) => props.theme.color.nav.background};
`;
CountDownBox.displayName = 'CountDownBox';

const ParagraphNoMargin = styled.p`
    margin: 0;
`;

const padZeroStart = (number, nroDigits = 2) => `0${number}`.slice(-nroDigits);
const getCountDownTime = (event) => {
  return (
    <>
      {padZeroStart(event.hours)}:{padZeroStart(event.minutes)}:{padZeroStart(event.seconds)}
    </>
  );
};

function formatStartTime(datetime, utcOffset) {
  return formatDatetimeWithUtcOffset(datetime, utcOffset, 'hh:mm A [UTC]Z');
}

function formatStartDate(datetime, utcOffset) {
  return formatDatetimeWithUtcOffset(datetime, utcOffset, 'D MMM YYYY');
}

export function EventStartsAtDatetimeText({ startDate, startDateUtcOffset }) {
  return (
    <RegularText fontSize="14px" align="left">
      <ParagraphNoMargin>
        This event starts
        <br />
        on <strong>{formatStartDate(startDate, startDateUtcOffset)}</strong>
        <br />
        at <strong>{formatStartTime(startDate, startDateUtcOffset)}</strong>
      </ParagraphNoMargin>
    </RegularText>
  );
}

// TODO: Move these global variables to inside the component, using useRef
let startCountDownInterval;
let endCountDownInterval;
export default function CountDown({
  endDate,
  startDate,
  startDateUtcOffset = UTC_ZERO_OFFSET,
  allowStartingPreview = false,
  isAdminView = false,
  mobile = false,
}) {
  const [eventEndCountDown, setEndCountDown] = useState();
  const [eventStartCountDown, setStartCountDown] = useState();
  const isEventHappening = useMemo(
    () => isTodayBetween(startDate, endDate),
    [startDate, endDate, eventEndCountDown]
  );

  const isEndingEvent = useMemo(() => {
    if (eventEndCountDown) {
      return (
        Number(
          `${padZeroStart(eventEndCountDown.months)}${padZeroStart(
            eventEndCountDown.days
          )}${padZeroStart(eventEndCountDown.hours)}${padZeroStart(
            eventEndCountDown.minutes
          )}${padZeroStart(eventEndCountDown.seconds)}`
        ) < FIVE_MINUTES
      );
    }
    return false;
  }, [eventEndCountDown]);

  const startEventCountDown = () =>
    setInterval(() => {
      setEndCountDown(
        intervalToDuration({
          start: new Date(),
          end: new Date(endDate),
        })
      );
    }, 1000);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!endDate) {
      return;
    }
    // event is happening: set end count down interval
    if (isEventHappening) {
      clearInterval(endCountDownInterval);
      endCountDownInterval = startEventCountDown();
      return;
    }
    // event is not happening: disable end count down interval and set start count down interval
    clearInterval(endCountDownInterval);
    setEndCountDown(null);
    clearInterval(startCountDownInterval);
    startCountDownInterval = setInterval(() => {
      setStartCountDown(
        intervalToDuration({
          start: new Date(),
          end: new Date(startDate),
        })
      );
    }, 1000);
  }, [startDate, endDate, isEventHappening]);

  // unmount function
  useEffect(
    () => () => {
      clearInterval(endCountDownInterval);
      clearInterval(startCountDownInterval);
    },
    []
  );

  // check if event starts
  useEffect(() => {
    // clear start count down interval and set end count down interval
    if (new Date() > new Date(startDate) && endDate) {
      clearInterval(startCountDownInterval);
      setStartCountDown(null);
      clearInterval(endCountDownInterval);
      endCountDownInterval = startEventCountDown();
    }
  }, [eventStartCountDown]);

  if (eventEndCountDown) {
    const endCountDownInDays = differenceInDays(new Date(endDate), new Date());
    if (isEventHappening) {
      return mobile ? (
        <div className="CountDown-mobileContainer" style={{ display: 'flex' }}>
          <SandTimeIcon />
          <RegularText light style={{ marginLeft: '8px' }}>
            {getCountDownTime(eventEndCountDown)}
          </RegularText>
        </div>
      ) : (
        <CountDownBox>
          <Flex mb="4px" className="CountDown-finishingLabel">
            <RegularText fontSize="14px">Finishing in:</RegularText>
          </Flex>
          {isEndingEvent ? (
            <ShakeContent shake>
              <RegularText fontSize="28px" mediumWeight color="#E06C75">
                {getCountDownTime(eventEndCountDown)}
              </RegularText>
            </ShakeContent>
          ) : endCountDownInDays > 0 ? (
            <RegularText
              fontSize="28px"
              mediumWeight
              {...(isAdminView ? { color: '#66bb6a' } : { light: true })}
            >{`${endCountDownInDays} ${endCountDownInDays > 1 ? 'days' : 'day'}`}</RegularText>
          ) : (
            <RegularText
              fontSize="28px"
              mediumWeight
              {...(isAdminView ? { color: '#66bb6a' } : { light: true })}
            >
              {getCountDownTime(eventEndCountDown)}
            </RegularText>
          )}
        </CountDownBox>
      );
    }
    return (
      <CountDownBox height="40px">
        <Flex mb="4px" className="CountDown-endedLabel">
          <RegularText fontSize="14px">Event has ended</RegularText>
        </Flex>
      </CountDownBox>
    );
  }

  if (eventStartCountDown && (allowStartingPreview || isAdminView)) {
    const startCountDownInDays = differenceInDays(new Date(startDate), new Date());
    return (
      <CountDownBox height="auto">
        <Flex mb="4px" className="CountDown-startingLabel">
          <RegularText fontSize="14px">Starting in:</RegularText>
        </Flex>
        <Flex>
          {startCountDownInDays > 0 ? (
            <RegularText fontSize="28px" mediumWeight light>{`${startCountDownInDays} ${
              startCountDownInDays > 1 ? 'days' : 'day'
            }`}</RegularText>
          ) : (
            <RegularText fontSize="28px" mediumWeight light>
              {getCountDownTime(eventStartCountDown)}
            </RegularText>
          )}
        </Flex>
        <Flex my="4px" className="CountDown-startDatetimeLabel">
          <EventStartsAtDatetimeText startDate={startDate} startDateUtcOffset={startDateUtcOffset} />
        </Flex>
      </CountDownBox>
    );
  }

  return null;
}
